interface PathRun {
  start: number;
  end: number;
}

// A LineRangePoints is a collection of "strokes" (sequences of points) that draw the background boxes behind
// residue glyphs on a sequence map. In practice, they're always rectangles, and at this step, they're
// normalized with the x-axis in bases and the y-axis as 0 or 1 rather than being measured in pixels. They are
// later transformed to pixels.
type LineRangePoint = [number, number];
export type LineRangePoints = Array<[LineRangePoint, LineRangePoint, LineRangePoint, LineRangePoint]>;

/**
 * A class to collect sets of base pair indices to draw rectangles over or behind. Contiguous indices are
 * 'merged' into one rectangle, or 'run', in order to draw fewer individual rectangles.
 */
export class PathBuilder {
  lastRun: PathRun | null = null;
  pointRuns: Array<PathRun> = [];

  /**
   * Add a (base pair) index to the PathBuilder.
   */
  addIndex(i: number): void {
    if (this.lastRun != null && this.lastRun.end === i) {
      this.lastRun.end++;
    } else {
      this.lastRun = {start: i, end: i + 1};
      this.pointRuns.push(this.lastRun);
    }
  }

  /**
   * Gets the data string (`d` attribute) for the svg `path` represented by the indices passed in as
   * lineRangePoints. This path is a series of rectangles covering all indices added, with 'runs' of
   * contiguous indices sharing one merged rectangle.
   * @param xTransform A function to transform from base pair indices to pixels.
   * @param yTransform A function to transform from y-index (0 or 1 - bottom or top) to pixels.
   * @param lineRangePoints The list of `LineRangePoints` to convert to a data string.
   */
  static getDataString(
    lineRangePoints: LineRangePoints,
    xTransform: (x: number) => number,
    yTransform: (y: number) => number
  ): string {
    let path = '';
    for (const set of lineRangePoints) {
      // Start the path at the bottom left.
      path += `M${xTransform(set[0][0])},${yTransform(set[0][1])}`;
      let lastY = -1;
      let refX = -1;
      for (const [x, y] of set.slice(1)) {
        const thisX = Math.max(refX + 1, xTransform(x));
        const thisY = yTransform(y);
        if (thisY !== lastY) {
          refX = xTransform(x);
        }
        path += `L${thisX},${thisY}`;
        lastY = thisY;
      }
    }
    return path;
  }

  /**
   * Get the bp / (top | bottom) points for all rectangles in the path.
   */
  getPoints(): LineRangePoints {
    return this.pointRuns.map((run) => [
      [run.start, 0],
      [run.start, 1],
      [run.end, 1],
      [run.end, 0],
    ]);
  }
}
